import { Banner } from '@components/display/banners/Banner';
import { Tabs } from '@components/utility/tabs';
import { APP_PREFIX_PATH } from '@constants/common';
import VictronWebsiteApiImage from '@developers/assets/images/victron-website-api.png';
import { VictronWebsiteAPITabContent } from '@developers/routes/VictronWebsiteAPI/VictronWebsiteAPITabContent';
import { VictronWebsiteApiTabValues } from '@developers/types';
import { useAppDispatch } from '@hooks/generic';
import { Box, Stack, Typography } from '@mui/material';
import { setBreadcrumbs } from '@store/breadcrumbSlice';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

export const VictronWebsiteAPI = () => {
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const currentTab = (() => {
        const tab = searchParams.get('tab');

        return tab && Object.values(VictronWebsiteApiTabValues).includes(tab as VictronWebsiteApiTabValues)
            ? (tab as VictronWebsiteApiTabValues)
            : VictronWebsiteApiTabValues.ProductInformation;
    })();

    const handleTabChange = (_event: React.SyntheticEvent, value: VictronWebsiteApiTabValues) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('tab', value);
        setSearchParams(nextParams);
    };

    useEffect(() => {
        dispatch(
            setBreadcrumbs([
                {
                    label: intl.formatMessage({ id: 'developers', defaultMessage: 'Developers' }),
                    to: APP_PREFIX_PATH + '/developers',
                    skipParams: true,
                },
                {
                    label: intl.formatMessage({
                        id: 'developers.victronwebsiteapi.breadcrumb',
                        defaultMessage: 'Victron Website API',
                    }),
                    to: APP_PREFIX_PATH + '/developers/api-victron/',
                },
            ])
        );
    }, [dispatch, intl]);

    return (
        <>
            <title>
                {intl.formatMessage({
                    id: 'developers.victronwebsiteapi.title',
                    defaultMessage: 'Victron Energy Professional - Developers - Victron Website API',
                })}
            </title>
            <Box component="main" p="16px">
                <Banner
                    data={{
                        header_title: (
                            <FormattedMessage
                                id="developers.victronwebsiteapi.header_title"
                                defaultMessage="Victron Website API"
                            />
                        ),
                        header_subtitle: (
                            <FormattedMessage
                                id="developers.victronwebsiteapi.header_subtitle"
                                defaultMessage="Everything you need to integrate Victron Products"
                            />
                        ),
                        header_text: (
                            <FormattedMessage
                                id="developers.victronwebsiteapi.header_text"
                                defaultMessage="Our comprehensive API provides access to product data, high-quality images, and technical documentation"
                            />
                        ),
                        button_text: (
                            <FormattedMessage
                                id="developers.victronwebsiteapi.button_text"
                                defaultMessage="Start with Victron Website API"
                            />
                        ),
                        button_link: 'https://www.victronenergy.com/api/v1/root',
                        background: 'light-blue',
                        image: VictronWebsiteApiImage,
                        image_alt: 'Victron Website API Banner',
                    }}
                    imgProps={{
                        className: '!object-contain !bg-transparent',
                    }}
                    buttonProps={{
                        target: '_blank',
                    }}
                />
                <Typography fontFamily="museo-sans" fontSize={32} letterSpacing={-1} pt="24px" pb="16px">
                    <FormattedMessage id="developers.victronwebsiteapi.api_endpoints" defaultMessage="API Endpoints" />
                </Typography>
                <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable">
                    <Tabs.Tab
                        value={VictronWebsiteApiTabValues.ProductInformation}
                        label={
                            <FormattedMessage
                                id="developers.victronwebsiteapi.product_information"
                                defaultMessage="Product Information"
                            />
                        }
                    />
                    <Tabs.Tab
                        value={VictronWebsiteApiTabValues.ProductPhotos}
                        label={
                            <FormattedMessage
                                id="developers.victronwebsiteapi.product_photos"
                                defaultMessage="Product Photos"
                            />
                        }
                    />
                    <Tabs.Tab
                        value={VictronWebsiteApiTabValues.ProductDocuments}
                        label={
                            <FormattedMessage
                                id="developers.victronwebsiteapi.product_documents"
                                defaultMessage="Product Documents"
                            />
                        }
                    />
                </Tabs>
                <Stack direction={{ xs: 'column', lg: 'row' }} pt="30px" spacing="14px">
                    <VictronWebsiteAPITabContent tab={currentTab} />
                </Stack>
            </Box>
        </>
    );
};
